// src/services/http.ts
// Wrapper fetch con base URL dinamica + gestione refresh token.

import { tokenManager } from '../context/AuthContext';

type RefreshFn = () => Promise<boolean>;
let refreshAuth: RefreshFn = async () => false;

type UnauthorizedFn = () => void;
let onUnauthorized: UnauthorizedFn | null = null;

/** Registra la funzione di refresh fornita dall'AuthProvider */
export const registerRefresh = (fn: RefreshFn) => {
  refreshAuth = fn;
};

/** Registra un handler da invocare quando il refresh fallisce e permane il 401 */
export const registerUnauthorized = (fn: UnauthorizedFn | null) => {
  onUnauthorized = fn;
};

/** Rileva FormData per non settare manualmente il Content-Type */
const isFormDataBody = (b: unknown): b is FormData =>
  typeof FormData !== 'undefined' && b instanceof FormData;

/** Endpoint pubblici (guest) → niente retry su 401 */
const isPublicPath = (path: string) => path.startsWith('/g/') || path.includes('/g/');

/** Base URL dinamica:
 * - DEV: usa VITE_API_URL o REACT_APP_API_URL se presenti
 * - PROD: same-origin + '/api' (evita CORS)
 */
function getApiBase(): string {
  // Vite
  // @ts-ignore
  const vite = typeof import.meta !== 'undefined' ? import.meta.env?.VITE_API_URL : undefined;
  // CRA
  // @ts-ignore
  const cra = typeof process !== 'undefined' ? process.env?.REACT_APP_API_URL : undefined;

  const env = (vite || cra || '').toString().trim();
  if (env) return env.replace(/\/+$/, ''); // rimuove slash finale

  // default same-origin
  return `${window.location.origin}/api`;
}

/** Esporta anche la costante per eventuali usi esterni */
export const API_BASE_URL = getApiBase();

/** Join sicuro base + path (garantisce uno slash solo) */
function buildUrl(path: string) {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${p}`;
}

/** Deduplica chiamate refresh concorrenti */
let refreshInFlight: Promise<boolean> | null = null;

/** Core fetch con Authorization + retry su 401 (solo /api/p/**) */
export async function apiRequest(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const makeHeaders = () => {
    const headers = new Headers(options.headers as any);
    const body = options.body as unknown;

    // Non impostare Content-Type se body è FormData (serve boundary)
    if (body != null && !isFormDataBody(body) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    // Authorization se ho l'access token e non è già impostato
    const access = tokenManager.getAccess?.();
    if (access && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${access}`);
    }

    // Accept JSON di default (non obbligatorio ma utile)
    if (!headers.has('Accept')) {
      headers.set('Accept', 'application/json, text/plain, */*');
    }

    return headers;
  };

  const doFetch = () =>
    fetch(buildUrl(path), {
      ...options,
      headers: makeHeaders(),
      // In same-origin non serve CORS; lasciamo default delle credenziali
      credentials: 'same-origin',
    });

  let res = await doFetch();

  // Se 401 e NON è endpoint pubblico → prova un refresh una sola volta
  if (res.status === 401 && !isPublicPath(path)) {
    if (!refreshInFlight) {
      refreshInFlight = refreshAuth().finally(() => {
        refreshInFlight = null;
      });
    }
    const ok = await refreshInFlight;
    if (ok) {
      // Ripeti la richiesta con l'eventuale nuovo token
      res = await doFetch();
    }

    if (res.status === 401) {
      onUnauthorized?.();
    }
  }

  return res;
}

/** Convenienza: parse JSON + gestione errori */
export async function apiJson<T = any>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await apiRequest(path, options);
  const text = await res.text().catch(() => '');

  let data: any = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      // risposta non JSON: lascio data = null
    }
  }

  if (!res.ok) {
    const msg =
      (data && (data.message || data.error)) ||
      text ||
      `HTTP ${res.status}`;
    throw new Error(msg);
  }

  // 204 No Content
  if (res.status === 204) return undefined as unknown as T;

  return data as T;
}

/** Metodi helper */
export const http = {
  get:   <T = any>(path: string) => apiJson<T>(path),
  post:  <T = any>(path: string, body?: any) =>
    apiJson<T>(path, {
      method: 'POST',
      body: isFormDataBody(body) ? body : JSON.stringify(body ?? {}),
    }),
  put:   <T = any>(path: string, body?: any) =>
    apiJson<T>(path, {
      method: 'PUT',
      body: isFormDataBody(body) ? body : JSON.stringify(body ?? {}),
    }),
  patch: <T = any>(path: string, body?: any) =>
    apiJson<T>(path, {
      method: 'PATCH',
      body: isFormDataBody(body) ? body : JSON.stringify(body ?? {}),
    }),
  delete:<T = any>(path: string) =>
    apiJson<T>(path, { method: 'DELETE' }),
};
